import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

const TYPE_LABELS = {
  conge: "Congé",
  conge_paye: "Congé payé",
  absence_maladie: "Absence maladie",
};
const STATUS_LABELS = { pending: "En attente", approved: "Accepté", rejected: "Refusé" };

export default function ChefConges() {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/chef/conges");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, status) => {
    try {
      await api.post(`/chef/conges/${id}/action`, { status });
      toast.success(status === "approved" ? "Demande acceptée" : "Demande refusée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pending = items.filter((i) => i.status === "pending");
  const processed = items.filter((i) => i.status !== "pending");

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Congés" title="Congés & absences" sub="Validez ou refusez les demandes de congés et absences signalées par vos salariés." />
      <div className="p-6 lg:p-10 space-y-8">
        <Section title="En attente" items={pending} onAct={act} testid="conges-pending" />
        <Section title="Traitées" items={processed} readOnly testid="conges-processed" />
      </div>
    </div>
  );
}

function Section({ title, items, onAct, readOnly, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-3">{title} ({items.length})</div>
      <div className="bg-white border border-[#DEE2E6]" data-testid={testid}>
        {items.length === 0 ? (
          <div className="p-6 text-sm text-[#868E96] text-center">Aucune demande.</div>
        ) : items.map((i) => (
          <div key={i.id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#DEE2E6] items-center text-sm last:border-b-0" data-testid={`conge-row-${i.id}`}>
            <div className="col-span-3 font-semibold">{i.salarie ? `${i.salarie.prenom} ${i.salarie.nom}` : "—"}</div>
            <div className="col-span-2">{TYPE_LABELS[i.type] || i.type}</div>
            <div className="col-span-3 font-mono text-xs">{i.date_debut} → {i.date_fin}</div>
            <div className="col-span-2 text-[#495057] truncate">{i.motif || "—"}</div>
            <div className="col-span-1"><StatusPill s={i.status} /></div>
            <div className="col-span-1 text-right">
              {!readOnly && (
                <div className="flex gap-1 justify-end">
                  <Button size="sm" onClick={() => onAct(i.id, "approved")} data-testid={`conge-approve-${i.id}`} className="bg-[#059669] hover:bg-[#047857] text-white h-8 px-2"><Check className="h-3 w-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => onAct(i.id, "rejected")} data-testid={`conge-reject-${i.id}`} className="h-8 px-2 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><X className="h-3 w-3" /></Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const cls = s === "approved" ? "status-active" : s === "rejected" ? "status-blocked" : "status-pending";
  return <span className={`status-pill ${cls}`}>{STATUS_LABELS[s]}</span>;
}
